// Simplified secure logging utility to prevent app crashes
// Use Vite's import.meta.env.MODE for proper environment detection
const isDevelopment = import.meta.env.MODE === 'development' || import.meta.env.DEV;

// Import supabase for auth checks (lazy loaded to avoid circular dependencies)
let supabaseClient = null;
if (typeof window !== 'undefined') {
  // Lazy load supabase to avoid circular dependencies
  import('../lib/supabase').then(module => {
    supabaseClient = module.supabase;
    if (window.supabase) {
      window.supabase = supabaseClient;
    }
  }).catch(() => {
    // Supabase not available, will use fallback auth checks
  });
}

// Check if user is authenticated (async check)
// SECURITY: Only authenticated users should be able to enable debug logs
const isUserAuthenticated = async () => {
  try {
    // Try to get supabase client (lazy loaded)
    let supabase = supabaseClient;
    if (!supabase && typeof window !== 'undefined') {
      try {
        const supabaseModule = await import('../lib/supabase');
        supabase = supabaseModule.supabase;
        supabaseClient = supabase; // Cache it
      } catch {
        // Supabase not available
      }
    }
    
    // Check for Supabase session (most reliable)
    if (supabase) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session && session.user) {
          authCheckCache = { result: true, timestamp: Date.now() };
          return true;
        }
      } catch {
        // Supabase check failed, try fallback
      }
    }
    
    // Fallback: Check for local session
    try {
      const { validateSession } = await import('./security');
      const session = await validateSession();
      if (session && session.user) {
        authCheckCache = { result: true, timestamp: Date.now() };
        return true;
      }
    } catch {
      // Session validation failed
    }
    
    // Last resort: Check for session in localStorage (weak but better than nothing)
    const hasSession = localStorage.getItem('session') || 
                       sessionStorage.getItem('userSession') ||
                       localStorage.getItem('userEmail');
    const result = !!hasSession;
    authCheckCache = { result, timestamp: Date.now() };
    return result;
  } catch {
    authCheckCache = { result: false, timestamp: Date.now() };
    return false;
  }
};

// Check if debug logging is enabled via URL parameter or localStorage
// SECURITY: In production, debug logs are DISABLED by default to prevent information leakage
// SECURITY: Even when enabled, requires authentication and data is still sanitized
const isDebugEnabled = async () => {
  // Always enabled in development
  if (isDevelopment) return true;
  
  // SECURITY: In production, debug logs are DISABLED by default
  // This prevents sensitive data from being exposed in browser console
  // Users must explicitly enable debug logging AND be authenticated (and it will still be sanitized)
  if (typeof window !== 'undefined') {
    // Check URL parameter (e.g., ?debug=true) - requires explicit opt-in AND authentication
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('debug') === 'true') {
      // SECURITY: Require authentication before enabling
      const authenticated = await isUserAuthenticated();
      if (!authenticated) {
        console.warn('⚠️ SECURITY: Debug logging requires authentication. Please log in first.');
        return false;
      }
      // Even when enabled, data is still sanitized
      return true;
    }
    
    // Check localStorage flag (can be set via browser console for debugging)
    // SECURITY: User must explicitly enable this AND be authenticated
    const debugEnabled = localStorage.getItem('enableDebugLogs');
    if (debugEnabled === 'true') {
      // SECURITY: Check if it's expired (auto-disable after 1 hour for safety)
      const enabledAt = localStorage.getItem('enableDebugLogs_timestamp');
      if (enabledAt) {
        const oneHour = 60 * 60 * 1000;
        if (Date.now() - parseInt(enabledAt, 10) > oneHour) {
          // Auto-disable after 1 hour
          localStorage.removeItem('enableDebugLogs');
          localStorage.removeItem('enableDebugLogs_timestamp');
          console.warn('⚠️ Debug logging auto-disabled after 1 hour for security.');
          return false;
        }
      }
      
      // SECURITY: Require authentication before enabling
      const authenticated = await isUserAuthenticated();
      if (!authenticated) {
        // Clear the flag if user is not authenticated
        localStorage.removeItem('enableDebugLogs');
        localStorage.removeItem('enableDebugLogs_timestamp');
        return false;
      }
      // Even when enabled, data is still sanitized
      return true;
    }
  }
  
  // Production default: no debug logs
  return false;
};

// Cache the authentication check result (refresh every 5 minutes)
let authCheckCache = { result: null, timestamp: 0 };
const AUTH_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const isDebugEnabledSync = () => {
  // Always enabled in development
  if (isDevelopment) return true;
  
  if (typeof window !== 'undefined') {
    // Check URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('debug') === 'true') {
      // Use cached auth result for sync check
      if (Date.now() - authCheckCache.timestamp < AUTH_CACHE_TTL && authCheckCache.result !== null) {
        return authCheckCache.result;
      }
      // If cache is stale, return false and let async check handle it
      return false;
    }
    
    // Check localStorage flag
    const debugEnabled = localStorage.getItem('enableDebugLogs');
    if (debugEnabled === 'true') {
      // Check expiration
      const enabledAt = localStorage.getItem('enableDebugLogs_timestamp');
      if (enabledAt) {
        const oneHour = 60 * 60 * 1000;
        if (Date.now() - parseInt(enabledAt, 10) > oneHour) {
          localStorage.removeItem('enableDebugLogs');
          localStorage.removeItem('enableDebugLogs_timestamp');
          return false;
        }
      }
      // Use cached auth result
      if (Date.now() - authCheckCache.timestamp < AUTH_CACHE_TTL && authCheckCache.result !== null) {
        return authCheckCache.result;
      }
      return false;
    }
  }
  
  return false;
};

// List of sensitive keywords that should be redacted
const SENSITIVE_KEYWORDS = [
  'password',
  'secret',
  'key',
  'token',
  'credential',
  'admin_password',
  'platformadmin_password',
  'stripe_secret',
  'webhook_secret'
];

// List of sensitive field names that should always be redacted
const SENSITIVE_FIELDS = [
  'email',
  'userEmail',
  'emailAddress',
  'sessionId',
  'session_id',
  'sessionToken',
  'userId',
  'user_id',
  'id',
  'customerId',
  'customer_id',
  'stripeCustomerId',
  'stripe_customer_id',
  'subscriptionId',
  'subscription_id',
  'stripeSubscriptionId',
  'stripe_subscription_id',
  'planId',
  'plan_id',
  'priceId',
  'price_id',
  'receiptId',
  'receipt_id',
  'accessToken',
  'access_token',
  'refreshToken',
  'refresh_token',
  'authToken',
  'auth_token',
  'apiKey',
  'api_key',
  'privateKey',
  'private_key',
  'secretKey',
  'secret_key'
];

// Email regex pattern
const EMAIL_REGEX = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;

// UUID pattern (for session IDs, user IDs, etc.)
const UUID_REGEX = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

// Stripe ID patterns (cus_, sub_, price_, etc.)
const STRIPE_ID_REGEX = /\b(cus_|sub_|price_|pi_|pm_|ch_)[a-zA-Z0-9]{24,}\b/g;

// Long alphanumeric strings (likely session tokens or IDs)
const LONG_TOKEN_REGEX = /\b[a-zA-Z0-9]{32,}\b/g;

// Function to redact email addresses
const redactEmail = (str) => {
  return str.replace(EMAIL_REGEX, (match) => {
    const [local, domain] = match.split('@');
    return `${local.substring(0, 2)}***@${domain}`;
  });
};

// Function to redact UUIDs
const redactUUID = (str) => {
  return str.replace(UUID_REGEX, '[UUID_REDACTED]');
};

// Function to redact Stripe IDs
const redactStripeId = (str) => {
  return str.replace(STRIPE_ID_REGEX, (match) => {
    const prefix = match.substring(0, 4);
    return `${prefix}***[REDACTED]`;
  });
};

// Function to redact long tokens
const redactLongToken = (str) => {
  return str.replace(LONG_TOKEN_REGEX, (match) => {
    if (match.length >= 32) {
      return `${match.substring(0, 4)}***[REDACTED]`;
    }
    return match;
  });
};

// Function to sanitize sensitive data
const sanitizeData = (data) => {
  // Handle strings - redact patterns
  if (typeof data === 'string') {
    let sanitized = data;
    // Redact emails
    sanitized = redactEmail(sanitized);
    // Redact UUIDs
    sanitized = redactUUID(sanitized);
    // Redact Stripe IDs
    sanitized = redactStripeId(sanitized);
    // Redact long tokens (but not if it's already been redacted)
    if (!sanitized.includes('[REDACTED]')) {
      sanitized = redactLongToken(sanitized);
    }
    // Redact if it contains sensitive keywords
    if (SENSITIVE_KEYWORDS.some(keyword => sanitized.toLowerCase().includes(keyword) && sanitized.length > 20)) {
      return '[REDACTED]';
    }
    return sanitized;
  }
  
  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(sanitizeData);
  }
  
  // Handle objects
  if (typeof data === 'object' && data !== null) {
    const sanitized = {};
    for (const [key, value] of Object.entries(data)) {
      const lowerKey = key.toLowerCase();
      
      // Always redact sensitive fields
      if (SENSITIVE_FIELDS.some(field => lowerKey === field.toLowerCase() || lowerKey.includes(field.toLowerCase()))) {
        if (typeof value === 'string') {
          // Redact the value but keep the key
          sanitized[key] = '[REDACTED]';
        } else if (typeof value === 'object' && value !== null) {
          // For objects, recursively sanitize but mark as redacted
          sanitized[key] = '[REDACTED_OBJECT]';
        } else {
          sanitized[key] = '[REDACTED]';
        }
      } else if (SENSITIVE_KEYWORDS.some(keyword => lowerKey.includes(keyword))) {
        sanitized[key] = '[REDACTED]';
      } else {
        // Recursively sanitize nested values
        sanitized[key] = sanitizeData(value);
      }
    }
    return sanitized;
  }
  
  return data;
};

// Initialize auth check on load (non-blocking)
// Note: Banner functions are defined later in the file, so we don't call them here
// The window.load event handler will take care of showing the banner if needed
if (typeof window !== 'undefined') {
  // Check if debug is requested via URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const debugRequested = urlParams.get('debug') === 'true';
  const debugEnabled = localStorage.getItem('enableDebugLogs') === 'true';
  
  // If debug is requested, do auth check immediately and cache result
  if (debugRequested || debugEnabled) {
    isUserAuthenticated().then(authenticated => {
      if (authenticated && debugRequested) {
        // If authenticated and debug requested via URL, enable it
        localStorage.setItem('enableDebugLogs', 'true');
        localStorage.setItem('enableDebugLogs_timestamp', Date.now().toString());
        // Banner will be shown by window.load event handler
      } else if (!authenticated) {
        // Clear debug flag if not authenticated
        if (debugEnabled) {
          localStorage.removeItem('enableDebugLogs');
          localStorage.removeItem('enableDebugLogs_timestamp');
        }
      }
    }).catch(() => {
      // Ignore errors
    });
  } else {
    // Do initial auth check in background (for caching)
    isUserAuthenticated().catch(() => {
      // Ignore errors
    });
  }
}

// Simplified secure logging functions that won't break the app
// NOTE: These are synchronous for performance, but use cached auth results
export const secureLog = {
  info: (...args) => {
    try {
      if (isDebugEnabledSync()) {
        const sanitizedArgs = args.map(arg => {
          try {
            return sanitizeData(arg);
          } catch {
            return '[LOG_ERROR]';
          }
        });
        console.log(...sanitizedArgs);
      }
    } catch {
      // Never crash the app due to logging
    }
  },
  
  warn: (...args) => {
    try {
      if (isDebugEnabledSync()) {
        const sanitizedArgs = args.map(arg => {
          try {
            return sanitizeData(arg);
          } catch {
            return '[LOG_ERROR]';
          }
        });
        console.warn(...sanitizedArgs);
      }
    } catch {
      // Never crash the app due to logging
    }
  },
  
  error: (...args) => {
    try {
      // Always log errors but sanitize them
      const sanitizedArgs = args.map(arg => {
        try {
          return sanitizeData(arg);
        } catch {
          return '[LOG_ERROR]';
        }
      });
      console.error(...sanitizedArgs);
    } catch {
      // Last resort: use basic console.error
      console.error('Logging error occurred');
    }
  },
  
  debug: (...args) => {
    try {
      if (isDebugEnabledSync()) {
        const sanitizedArgs = args.map(arg => {
          try {
            return sanitizeData(arg);
          } catch {
            return '[LOG_ERROR]';
          }
        });
        console.debug('[DEBUG]', ...sanitizedArgs);
      }
    } catch {
      // Never crash the app due to logging
    }
  }
};

// Helper function to enable debug logging for a user (can be called from browser console)
// SECURITY: Only works for authenticated users, auto-disables after 1 hour
// Usage: window.enableDebugLogs() or localStorage.setItem('enableDebugLogs', 'true')
if (typeof window !== 'undefined') {
  window.enableDebugLogs = async () => {
    // SECURITY: Properly check if user is authenticated
    const authenticated = await isUserAuthenticated();
    
    if (!authenticated) {
      console.warn('⚠️ SECURITY: Debug logging requires authentication. Please log in first.');
      return false;
    }
    
    // Set flag with timestamp for auto-expiration
    localStorage.setItem('enableDebugLogs', 'true');
    localStorage.setItem('enableDebugLogs_timestamp', Date.now().toString());
    
    // Update auth cache
    authCheckCache = { result: true, timestamp: Date.now() };
    
    console.log('✅ Debug logging enabled. Refresh the page to see logs.');
    console.warn('⚠️ SECURITY: Debug logs will auto-disable after 1 hour for security.');
    console.warn('⚠️ SECURITY: Debug logs contain sensitive information. Only enable when debugging issues.');
    
    // Show warning banner in UI
    showDebugWarningBanner();
    
    return true;
  };
  
  window.disableDebugLogs = () => {
    localStorage.removeItem('enableDebugLogs');
    localStorage.removeItem('enableDebugLogs_timestamp');
    authCheckCache = { result: false, timestamp: Date.now() };
    console.log('✅ Debug logging disabled. Refresh the page.');
    hideDebugWarningBanner();
    return true;
  };
  
  // Show warning banner when debug is enabled
  const showDebugWarningBanner = () => {
    // Remove existing banner if any
    hideDebugWarningBanner();
    
    const banner = document.createElement('div');
    banner.id = 'debug-warning-banner';
    banner.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: #ff6b35;
      color: white;
      padding: 12px;
      text-align: center;
      z-index: 10000;
      font-weight: bold;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    `;
    banner.innerHTML = `
      ⚠️ DEBUG LOGGING ENABLED - Sensitive data may be visible in console. Auto-disables in 1 hour.
      <button onclick="window.disableDebugLogs()" style="margin-left: 12px; padding: 4px 8px; background: white; color: #ff6b35; border: none; border-radius: 4px; cursor: pointer; font-weight: bold;">Disable</button>
    `;
    document.body.appendChild(banner);
    
    // Add padding to body to prevent content from being hidden
    document.body.style.paddingTop = '48px';
  };
  
  const hideDebugWarningBanner = () => {
    const banner = document.getElementById('debug-warning-banner');
    if (banner) {
      banner.remove();
      document.body.style.paddingTop = '';
    }
  };
  
  // Check on page load if debug is enabled and show banner
  if (typeof window !== 'undefined') {
    window.addEventListener('load', async () => {
      const debugEnabled = localStorage.getItem('enableDebugLogs');
      if (debugEnabled === 'true') {
        const authenticated = await isUserAuthenticated();
        if (authenticated) {
          // Check if expired
          const enabledAt = localStorage.getItem('enableDebugLogs_timestamp');
          if (enabledAt) {
            const oneHour = 60 * 60 * 1000;
            if (Date.now() - parseInt(enabledAt, 10) <= oneHour) {
              showDebugWarningBanner();
            } else {
              // Auto-disable expired debug logs
              window.disableDebugLogs();
            }
          }
        } else {
          // Clear if not authenticated
          window.disableDebugLogs();
        }
      }
    });
  }
}

// Function to disable console methods in production (optional)
export const disableConsoleInProduction = () => {
  if (import.meta.env.MODE === 'production' || import.meta.env.PROD) {
    const noop = () => {};
    console.log = noop;
    console.warn = noop;
    console.info = noop;
    console.debug = noop;
    // Keep console.error for critical issues
  }
};

export default secureLog;